export const WEB_PUSH_PUBLIC_KEY = 'BGrlY-Df--BWnPVQjmSbAyBMQt-vGX4mzR1kaeGyKGmLbNagJdSt9ucxYDN9rjPPSRFLJ1V_rOoNT0cgD9oSlxg';

function isIos(nav) {
    return /iPad|iPhone|iPod/.test(nav.userAgent)
        || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);
}

export function needsIosInstall(win = window, nav = navigator) {
    const safari = /Safari/.test(nav.userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(nav.userAgent);
    return isIos(nav) && safari && !nav.standalone && !win.matchMedia?.('(display-mode: standalone)').matches;
}

export function supportsWebPush() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function applicationServerKey() {
    const value = WEB_PUSH_PUBLIC_KEY.replace(/-/g, '+').replace(/_/g, '/');
    return Uint8Array.from(atob(value + '='.repeat((4 - value.length % 4) % 4)), (char) => char.charCodeAt(0));
}

function deviceLabel() {
    const ua = navigator.userAgent;
    const browser = /Edg/.test(ua) ? 'Edge' : /Firefox|FxiOS/.test(ua) ? 'Firefox' : /Chrome|CriOS/.test(ua) ? 'Chrome' : /Safari/.test(ua) ? 'Safari' : 'Browser';
    const os = isIos(navigator) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'Unknown OS';
    return `${browser} / ${os}`;
}

export async function currentWebPush(client, { userId, orgId, accountId = null }) {
    if (!supportsWebPush() || Notification.permission !== 'granted') return false;
    if (!userId || !orgId) throw new Error('Sign in to your organization before checking device notifications.');
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return false;
    let query = client.from('push_tokens').select('id').eq('provider', 'webpush')
        .eq('token', subscription.endpoint).eq('user_id', userId).eq('org_id', orgId).eq('enabled', true);
    query = accountId ? query.eq('account_id', accountId) : query.is('account_id', null);
    const { data, error } = await query.maybeSingle();
    if (error) throw new Error(`Device notification status could not be loaded: ${error.message}`);
    return Boolean(data);
}

export async function webPushDeviceStatus(client, context) {
    if (needsIosInstall()) return 'install';
    if (!supportsWebPush()) return 'unsupported';
    if (Notification.permission === 'denied') return 'blocked';
    return await currentWebPush(client, context) ? 'on' : 'off';
}

/** BL-177 Step 2h: what the push worker last did with a push on THIS device (push_tokens.last_report), or null. Never throws. */
export async function readDeviceReport(client, { userId, orgId, accountId = null }) {
    try {
        if (!supportsWebPush() || !userId || !orgId) return null;
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();
        if (!subscription) return null;
        let query = client.from('push_tokens').select('last_report').eq('provider', 'webpush')
            .eq('token', subscription.endpoint).eq('user_id', userId).eq('org_id', orgId);
        query = accountId ? query.eq('account_id', accountId) : query.is('account_id', null);
        const { data } = await query.maybeSingle();
        return data && data.last_report && typeof data.last_report === 'object' ? data.last_report : null;
    } catch {
        return null;
    }
}

export async function disableWebPush(client, { userId, orgId, accountId = null }) {
    if (!userId || !orgId) throw new Error('Sign in to your organization before disabling notifications.');
    if (!supportsWebPush()) throw new Error('This browser does not support device notifications.');
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) throw new Error('This device has no browser subscription. Refresh its notification status.');
    let query = client.from('push_tokens').update({ enabled: false })
        .eq('provider', 'webpush').eq('token', subscription.endpoint)
        .eq('user_id', userId).eq('org_id', orgId);
    query = accountId ? query.eq('account_id', accountId) : query.is('account_id', null);
    const { data, error } = await query.select('id, enabled').maybeSingle();
    if (error) throw new Error(`Device notifications could not be turned off: ${error.message}`);
    if (!data || data.enabled !== false) throw new Error('This device registration was not disabled. Refresh and try again.');
}

export async function subscribeWebPush(client, { userId, orgId, accountId = null, clientApp = false }) {
    if (!userId || !orgId) throw new Error('Sign in to your organization before enabling notifications.');
    if (needsIosInstall()) throw new Error('In Safari, tap Share, then Add to Home Screen. Open the installed app to turn on notifications.');
    if (!supportsWebPush()) throw new Error('This browser does not support device notifications.');
    // Called only from a button. Request before awaiting registration to preserve
    // the user gesture required by Safari.
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') throw new Error('Notifications were not allowed. You can change this in your browser settings.');
    if (!clientApp) await navigator.serviceWorker.register(`${process.env.PUBLIC_URL || ''}/push-worker.js`);
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription()
        || await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: applicationServerKey() });
    try {
        const keys = subscription.toJSON().keys;
        if (!keys?.p256dh || !keys?.auth) throw new Error('The browser did not provide subscription keys.');
        if (accountId || clientApp) {
            // Portal / client-app subscriptions belong to an account, not a staff login: the claim RPC
            // does not cover them, so they keep the direct write.
            const { error } = await client.from('push_tokens').upsert({
                org_id: orgId, user_id: userId, account_id: accountId, driver_id: null,
                provider: 'webpush', token: subscription.endpoint, subscription_keys: keys,
                platform: 'web', device_label: deviceLabel(), enabled: true, last_seen_at: new Date().toISOString(),
            }, { onConflict: 'provider,token' });
            if (error) throw new Error(`Device notifications could not be saved: ${error.message}`);
        } else {
            // BL-177 Step 2c (A6): a browser keeps ONE push subscription however many people sign in on it.
            // The server claims this endpoint for the signed-in user (reassigning it from whoever had it).
            const { data, error } = await client.rpc('bl177_claim_web_push_subscription', {
                p_endpoint: subscription.endpoint, p_p256dh: keys.p256dh, p_auth: keys.auth,
                p_device_label: deviceLabel(), p_device_id: null,
            });
            if (error) throw new Error(`Device notifications could not be saved: ${error.message}`);
            if (!data || data.enabled !== true || data.registered_to_you !== true) {
                throw new Error('Device notifications could not be registered to you. Please try again.');
            }
        }
    } catch (error) {
        const removed = await subscription.unsubscribe();
        if (!removed) throw new Error(`${error.message} Browser subscription cleanup also failed.`);
        throw error;
    }
}

export async function unsubscribeWebPush(client, userId) {
    if (!supportsWebPush()) return;
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    const endpoint = subscription.endpoint;
    if (!await subscription.unsubscribe()) throw new Error('Browser notifications could not be turned off.');
    if (!userId) return;
    const { error } = await client.from('push_tokens').update({ enabled: false })
        .eq('provider', 'webpush').eq('token', endpoint).eq('user_id', userId);
    if (error) throw new Error(`Browser notifications are off, but device registration cleanup failed: ${error.message}`);
}

export function watchWebPushSignOut(client) {
    let previousUser = null;
    const { data } = client.auth.onAuthStateChange((event, session) => {
        const next = session?.user?.id ?? null;
        if (event === 'SIGNED_OUT' || (previousUser && next && next !== previousUser)) {
            // No database call with expired credentials, and no auth-lock reentry.
            setTimeout(() => unsubscribeWebPush(client, null).catch((error) => {
                console.error('Signed-out browser notification cleanup failed:', error.message);
            }), 0);
        }
        previousUser = next;
    });
    return () => data.subscription.unsubscribe();
}
